/** Seed data for the consolidated Procurement Exceptions hub (Demo Mode only). Brings supplier
 * exceptions and catalogue exceptions together into one list per the Stage 5 spec, with a
 * "Blocks go-live" flag so Procurement Overview/Readiness can separate general data-quality
 * issues from true launch blockers. No backend "procurement exceptions" feed exists yet - mock
 * only. Reuses the same severity/status vocabulary as
 * src/mocks/data/supplierProfileData.ts (supplier exceptions) for consistency. */

export type ExceptionType = 'Supplier' | 'Catalogue';
export type ExceptionSeverity = 'Critical' | 'High' | 'Medium' | 'Low';
export type ExceptionStatus = 'Open' | 'InProgress' | 'Waived' | 'Resolved';

export const EXCEPTION_SEVERITY_COLOR: Record<ExceptionSeverity, 'error' | 'warning' | 'info' | 'default'> = {
  Critical: 'error',
  High: 'warning',
  Medium: 'info',
  Low: 'default',
};

export const EXCEPTION_STATUS_COLOR: Record<ExceptionStatus, 'success' | 'warning' | 'default'> = {
  Open: 'warning',
  InProgress: 'warning',
  Waived: 'default',
  Resolved: 'success',
};

export interface ProcurementException {
  id: number;
  type: ExceptionType;
  exceptionType: string;
  severity: ExceptionSeverity;
  supplier?: string;
  product?: string;
  catalogue?: string;
  site: string;
  owner?: string;
  dueDate?: string;
  status: ExceptionStatus;
  blocksGoLive: boolean;
  suggestedResolution: string;
}

export const procurementExceptions: ProcurementException[] = [
  {
    id: 1, type: 'Supplier', exceptionType: 'Supplier not approved', severity: 'Critical',
    supplier: 'Coastal Seafoods Ltd', site: 'Northgate General', owner: 'M. Patel', dueDate: '2026-07-12',
    status: 'Open', blocksGoLive: true, suggestedResolution: 'Complete onboarding approval - insurance documents outstanding.',
  },
  {
    id: 2, type: 'Catalogue', exceptionType: 'Missing price', severity: 'Critical',
    supplier: 'Prime Cuts Butchery', product: 'Chicken Breast Fillets 5kg', catalogue: 'Healthcare Catering Catalogue',
    site: 'Downtown Community Clinic', owner: 'S. Wren', dueDate: '2026-07-10',
    status: 'InProgress', blocksGoLive: true, suggestedResolution: 'Chase commercial price approval for proposed increase.',
  },
  {
    id: 3, type: 'Catalogue', exceptionType: 'Product unavailable', severity: 'Critical',
    supplier: 'Coastal Seafoods Ltd', product: 'Mixed Spice Blend 1kg', catalogue: 'Healthcare Catering Catalogue',
    site: 'Northgate General', owner: 'M. Patel', dueDate: '2026-07-09',
    status: 'Open', blocksGoLive: true, suggestedResolution: 'Assign approved substitution while recall is in effect.',
  },
  {
    id: 4, type: 'Catalogue', exceptionType: 'Delivery schedule missing', severity: 'High',
    supplier: 'Northern Dairy Co-op', catalogue: 'Education Core Catalogue', site: 'Riverside School',
    owner: 'J. Okafor', dueDate: '2026-07-11',
    status: 'Open', blocksGoLive: true, suggestedResolution: 'Confirm delivery days/windows with supplier account manager.',
  },
  {
    id: 5, type: 'Supplier', exceptionType: 'Missing supplier product', severity: 'High',
    supplier: 'Harvest Grain Merchants', catalogue: 'Education Core Catalogue', site: 'Fairview Academy',
    owner: 'M. Patel', dueDate: '2026-07-14',
    status: 'Open', blocksGoLive: true, suggestedResolution: 'Configure order channel so products can be mapped.',
  },
  {
    id: 6, type: 'Catalogue', exceptionType: 'Off-contract product', severity: 'Medium',
    supplier: 'EcoPack Supplies', product: 'Biodegradable Napkins', catalogue: 'Corporate Dining Catalogue',
    site: 'Uptown Offices', owner: 'S. Wren', dueDate: '2026-07-18',
    status: 'InProgress', blocksGoLive: false, suggestedResolution: 'Review off-contract spend and add to catalogue or discontinue use.',
  },
  {
    id: 7, type: 'Catalogue', exceptionType: 'Allergen data missing', severity: 'High',
    supplier: 'Coastal Seafoods Ltd', product: 'Mixed Spice Blend 1kg', catalogue: 'Healthcare Catering Catalogue',
    site: 'Northgate General', owner: 'Compliance', dueDate: '2026-07-13',
    status: 'Open', blocksGoLive: false, suggestedResolution: 'Request updated allergen declaration from supplier.',
  },
  {
    id: 8, type: 'Supplier', exceptionType: 'Expired certifications', severity: 'Critical',
    supplier: 'Greenfield Farms', site: 'High Street Central', owner: 'Compliance', dueDate: '2026-07-09',
    status: 'Open', blocksGoLive: false, suggestedResolution: 'Request renewed BRCGS Food Safety certificate.',
  },
  {
    id: 9, type: 'Catalogue', exceptionType: 'Invalid unit of measure', severity: 'Low',
    supplier: 'EcoPack Supplies', product: 'Extra Virgin Olive Oil 5L', catalogue: 'Corporate Dining Catalogue',
    site: 'Uptown Offices', owner: 'S. Wren', dueDate: '2026-07-20',
    status: 'Resolved', blocksGoLive: false, suggestedResolution: 'Order unit corrected to litres to match supplier pack size.',
  },
  {
    id: 10, type: 'Supplier', exceptionType: 'Minimum order issue', severity: 'Medium',
    supplier: 'Prime Cuts Butchery', site: 'Downtown Community Clinic', owner: 'S. Wren', dueDate: '2026-07-16',
    status: 'Open', blocksGoLive: false, suggestedResolution: 'Consolidate with a nearby site to meet minimum order value.',
  },
  {
    id: 11, type: 'Catalogue', exceptionType: 'Catalogue not approved', severity: 'Medium',
    catalogue: 'Healthcare Catering Catalogue', site: 'Northgate General', owner: 'M. Patel', dueDate: '2026-07-15',
    status: 'InProgress', blocksGoLive: true, suggestedResolution: 'Complete catalogue governance review and publish.',
  },
  {
    id: 12, type: 'Supplier', exceptionType: 'Lead time issue', severity: 'Low',
    supplier: 'Harvest Grain Merchants', site: 'Fairview Academy', owner: 'M. Patel', dueDate: '2026-07-22',
    status: 'Waived', blocksGoLive: false, suggestedResolution: 'Accepted - site holds 2 weeks buffer stock for dry goods.',
  },
];
